<template>
  <v-layout>
    <MainMenu />
    <v-layout column>
      <v-layout class="none-growth">
        <v-flex>
          <listPagesHeader
            :page-title="pageTitle"
            :show-btn="false"
          />
        </v-flex>
      </v-layout>
      <div class="page-content-paddings">
        <ToAcceptFiltersBar
          v-if="$vuetify.breakpoint.smAndUp"
          @changeFilteredProvinces="filteredProvinces = $event"
          @changeShowExternalFamilies="showExternalFamilies = $event"
          @filtered="filtered()"
          @resetFilters="resetFilters()"
          @toggleExternalFamilies="fetchFamiliesToAccept(showExternalFamilies)"
        />
        <v-dialog
          v-else
          v-model="filterDialog"
          hide-overlay
          persistent
          scrollable
          fullscreen
        >
          <v-layout
            style="background: #fff; overflow: scroll"
            fill-height
          >
            <ToAcceptFiltersBar
              @changeFilteredProvinces="filteredProvinces = $event"
              @changeShowExternalFamilies="showExternalFamilies = $event"
              @filtered="filtered()"
              @resetFilters="resetFilters()"
              @toggleExternalFamilies="fetchFamiliesToAccept(showExternalFamilies)"
              @dialog="filterDialog = $event"
            />
          </v-layout>
        </v-dialog>
        <v-layout
          class="list-header"
          column
        >
          <v-layout
            justify-space-between
            align-center
          >
            <h2 class="d-inline-block">
              {{ $t('menu.families') }}
              <span v-if="!toAccept.isLoading">({{ toAccept.receivedDataAmount }})</span>
            </h2>
            <v-btn
              v-if="$vuetify.breakpoint.xs"
              flat
              class="mx-0 px-0 pb-3 text-none"
              color="sz_violet"
              style="min-width: 85px; height: 20px"
              @click="filterDialog = !filterDialog"
            >
              <BaseIcon>
                <FiltersIcon />
              </BaseIcon>
              {{ $t('filters.filter') }}
            </v-btn>
          </v-layout>
          <v-flex xs12>
            <p class="mt-2 mb-0">
              {{ $t('instructions.to_accept.start') }}
            </p>
            <ul>
              <li
                v-for="(item, index) in $t('instructions.to_accept.list')"
                :key="index"
              >
                {{ item }}
              </li>
            </ul>
            <p class="mt-2 mb-0">
              {{ $t('instructions.to_accept.end') }}
            </p>
          </v-flex>
        </v-layout>
        <v-layout>
          <v-flex>
            <toAcceptFamiliesTable
              :is-loading="toAccept.isLoading"
              :headers="toAccept.headers"
              :items="toAcceptFamilies"
              @verify-consent="changeFamilyInList($event)"
              @reload-families-to-accept="fetchFamiliesToAccept()"
              @sorting="sorting($event)"
            />
            <BasePagination
              :amount-all="amountAll"
              :pagination="toAccept.pagination"
              @page="setPage"
              @amount="setItemsPerPage"
            />
          </v-flex>
        </v-layout>
      </div>
    </v-layout>
  </v-layout>
</template>

<script>
import MainMenu from '@/components/widgets/menu/MainMenu';
import listPagesHeader from '@/components/widgets/lists/ListPagesHeader';
import toAcceptFamiliesTable from '@/components/widgets/families/toAcceptFamiliesTable';
import BasePagination from '@/components/widgets/dataTable/BasePagination';
import BaseIcon from '@/icons/BaseIcon';
import FiltersIcon from '@/icons/FiltersIcon';
import ToAcceptFiltersBar from '@/components/widgets/dataTable/ToAcceptFiltersBar';
import Permissions from '@/constants/permissions';

export default {
    name: 'FamiliesToApprovalPage',
    components: {
        MainMenu,
        listPagesHeader,
        toAcceptFamiliesTable,
        BasePagination,
        BaseIcon,
        FiltersIcon,
        ToAcceptFiltersBar
    },
    beforeRouteEnter (to, from, next) {
        next((vm) => {
            vm.$store.dispatch('auth/permissionGuard', [
                Permissions.FAMILY_TO_ACCEPT_INTERNAL,
                Permissions.FAMILY_TO_ACCEPT_EXTERNAL
            ]);
            vm.$store.dispatch('defaultApi/getProvincesForFamiliesToAccept');
        });
        next();
    },
    beforeRouteLeave (to, from, next) {
        this.$store.commit('familyData/setLocation', null);
        this.$store.commit('familyData/clearFormData');
        this.$store.commit('familyData/setSimilarityParameters', null);
        next();
    },
    data () {
        return {
            toAccept: {
                headers: [
                    {
                        text: this.$t('label.id'),
                        value: 'family_id',
                        sortable: false,
                        align: 'left',
                        desktopOnly: true
                    },
                    {
                        text: this.$t('label.family'),
                        value: 'last_name',
                        sortable: false,
                        sort: true,
                        icon: 'SortIcon',
                        desktopOnly: false
                    },
                    { text: this.$t('label.address'), value: 'address', sortable: false, desktopOnly: true },
                    { text: this.$t('label.scan'), value: '', sortable: false, desktopOnly: true },
                    { text: this.$t('label.reporter_email'), value: '', sortable: false, desktopOnly: true },
                    {
                        text: this.$t('label.created_at'),
                        value: 'created_at',
                        sortable: false,
                        desktopOnly: true
                    },
                    {
                        text: this.$t('label.scan') + ' | ' + this.$t('label.reporter_email'),
                        value: '',
                        sortable: false,
                        desktopOnly: false,
                        mobileOnly: true
                    },
                    { text: this.$t('label.action'), value: '', sortable: false, desktopOnly: false }
                ],
                actionMenu: [
                    { id: 1, text: this.$t('families_to_accept.tooltip.add') },
                    { id: 2, text: this.$t('families_to_accept.tooltip.remove') }
                ],
                pagination: { page: 1, amount: 20, amountAll: 0 },
                receivedDataAmount: '',
                isLoading: true
            },
            showExternalFamilies: false,
            filteredProvinces: null,
            filterDialog: false,
            sortingDirection: null
        };
    },
    computed: {
        pageTitle () {
            return this.$t('families_to_accept.title');
        },
        toAcceptFamilies () {
            return this.$store.getters['familiesToAccept/getFamiliesToAccept'] || [];
        },
        amountAll () {
            if (this.toAccept.receivedDataAmount) {
                return parseInt(this.toAccept.receivedDataAmount);
            }
            return 0;
        }
    },
    watch: {
        sortingDirection: {
            handler (val) {
                this.toAccept.headers[1].icon =
          val === 'desc' || val === null ? 'SortIconRevert' : 'SortIcon';
            },
            immediate: true
        }
    },
    created () {
        this.fetchFamiliesToAccept();
    },
    methods: {
        fetchFamiliesToAccept (external = false) {
            this.toAccept.isLoading = true;
            this.$store.commit('familiesToAccept/setFamiliesToAccept', []);
            const _offestNum = (this.toAccept.pagination.page - 1) * this.toAccept.pagination.amount;
            const _data = {
                limit: this.toAccept.pagination.amount,
                offset: _offestNum
            };
            if (external) {
                _data.external = external;
            }
            if (this.filteredProvinces != null && this.filteredProvinces.length > 0) {
                _data.provinces = this.filteredProvinces;
            }
            if (this.sortingDirection != null) {
                _data.order = { last_name: this.sortingDirection };
            }
            this.$store
                .dispatch('familiesToAccept/getFamiliesToAccept', _data)
                .then(() => {
                    this.toAccept.pagination.amountAll =
            this.$store.getters['familiesToAccept/getFamiliesAmount'];
                    this.toAccept.receivedDataAmount =
            this.$store.getters['familiesToAccept/getFamiliesAmount'];
                    if (this.toAcceptFamilies.length === 0 && this.toAccept.pagination.page > 1) {
                        this.toAccept.pagination.page -= 1;
                        this.fetchFamiliesToAccept(external);
                    }
                })
                .finally(() => {
                    this.toAccept.isLoading = false;
                });
        },
        setPage (event) {
            this.toAccept.pagination.page = event;
            this.fetchFamiliesToAccept(this.showExternalFamilies);
        },
        setItemsPerPage (event) {
            this.toAccept.pagination.amount = event;
            this.toAccept.pagination.page = 1;
            this.fetchFamiliesToAccept(this.showExternalFamilies);
        },
        filtered () {
            if (this.filteredProvinces != null && this.filteredProvinces.length > 0) {
                this.fetchFamiliesToAccept(this.showExternalFamilies);
            }
        },
        resetFilters () {
            this.fetchFamiliesToAccept();
        },
        sorting (event) {
            this.sortingDirection = event;
            this.fetchFamiliesToAccept(this.showExternalFamilies);
        },
        changeFamilyInList ($event) {
            const _families = this.toAcceptFamilies.map((el) => {
                if (el.family_to_accept_id === $event) {
                    el.verified_initial_consent = true;
                    return el;
                } else {
                    return el;
                }
            });
            this.$store.commit('familiesToAccept/setFamiliesToAccept', _families);
        }
    }
};
</script>

<style scoped></style>
